import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Pause, Plus } from "lucide-react";
import { useLang } from "@/components/LanguageContext";
import SEO from "@/components/SEO";
import ScrollReveal from "@/components/ScrollReveal";
import LeadCTA from "@/components/LeadCTA";
import GoogleReviews from "@/components/GoogleReviews";
import VideoShowcase from "@/components/VideoShowcase";
import EN from "@/data/content-en";
import ZH from "@/data/content-zh";
import { HOME_VIDEO, PAGE_IMAGES, pathFor } from "@/data/pages";

export default function HomePage() {
  const { lang, t } = useLang();
  const c = lang === "zh" ? ZH.home : EN.home;

  // Static copy for the PwC-style transforming section
  const transform = lang === "zh"
    ? {
        kicker: "技术与税务正在重塑商业世界",
        title: "税务与商业正在快速演变。",
        intro: "从企业架构到 SMSF，从负扣税到家族信托——您是否在以足够快的速度抓住增长机会？",
        insight: {
          kicker: "洞察",
          stat: "12+",
          body: "我们已服务澳大利亚个人与企业逾十二年——构建税务策略、保护资产、推动可持续增长。",
        },
        featured: {
          kicker: "精选思考",
          title: "您准备好迎接负扣税了吗？",
          body: "通过房产投资合理使用负扣税与折旧策略，是抓住长期资本增值的关键。",
          linkId: "negative-gearing",
        },
      }
    : {
        kicker: "Tax & business are rapidly transforming",
        title: "Tax and business are rapidly evolving.",
        intro: "From company structure to SMSF, from negative gearing to family trusts — are you evolving fast enough to capture compliant, tax-efficient growth?",
        insight: {
          kicker: "Insight",
          stat: "12+",
          body: "We've supported Australian individuals and businesses for over twelve years — building tax strategy, protecting assets and driving sustainable growth.",
        },
        featured: {
          kicker: "Featured thinking",
          title: "Are you negative-gearing fit?",
          body: "The key to long-term property investment lies in disciplined use of negative gearing and depreciation — without losing sight of capital growth.",
          linkId: "negative-gearing",
        },
      };

  return (
    <div className={lang === "zh" ? "lang-zh" : ""}>
      <SEO pageId="home" title={c.title} description={c.metaDescription} image={PAGE_IMAGES.home} />

      {/* Hero — PwC layout */}
      <section className="relative w-full h-[92vh] min-h-[640px] overflow-hidden bg-black" data-testid="home-hero">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover opacity-65"
        >
          <source src={HOME_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/70" />

        {/* Pause loop indicator top-right (desktop only) */}
        <div className="hidden md:flex absolute top-6 right-6 lg:right-10 text-white/90 text-[14px] items-center gap-2 select-none">
          <span>{lang === "zh" ? "暂停循环" : "Pause loop"}</span>
          <Pause className="h-3.5 w-3.5" />
        </div>

        {/* Main headline — large serif, left-aligned, bottom-anchored */}
        <div className="relative h-full max-w-[1480px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-40 md:pb-28">
          <ScrollReveal>
            <h1 className="serif text-white text-4xl sm:text-5xl md:text-7xl lg:text-[96px] leading-[1.02] tracking-tight max-w-5xl">
              {c.h1}
            </h1>
          </ScrollReveal>
        </div>

        {/* Bottom CTAs — stacked on mobile, anchored corners on desktop */}
        <div className="absolute left-0 right-0 bottom-0 md:bottom-auto">
          {/* Desktop: centred "Make it happen" */}
          <div
            className="hidden md:flex absolute left-1/2 -translate-x-1/2 bottom-6 items-center gap-3 text-white text-[14.5px] whitespace-nowrap"
            data-testid="hero-make-it"
          >
            <ArrowDown className="h-4 w-4 text-[var(--abs-orange)]" />
            <span>{t.cta.makeItHappen}</span>
          </div>

          {/* Desktop: bottom-right consult CTA */}
          <Link
            to={pathFor("contact", lang)}
            data-testid="hero-cta-consult"
            className="hidden md:inline-flex absolute right-6 lg:right-10 bottom-16 border border-[var(--abs-orange)] text-white px-7 py-3.5 items-center gap-3 hover:bg-[var(--abs-orange)] transition-colors"
          >
            <span className="text-[15px]">{t.cta.schedule}</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
          <div className="hidden md:block absolute right-6 lg:right-10 bottom-6 text-white/70 text-[12px]">(00:30)</div>

          {/* Mobile: stacked CTA pinned to bottom of hero */}
          <div className="md:hidden pb-6 px-6 flex flex-col gap-3" data-testid="hero-mobile-ctas">
            <Link
              to={pathFor("contact", lang)}
              data-testid="hero-cta-consult-mobile"
              className="inline-flex items-center justify-center gap-2 border border-[var(--abs-orange)] text-white py-3 hover:bg-[var(--abs-orange)] transition-colors"
            >
              <span className="text-[14.5px]">{t.cta.schedule}</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
            <div className="flex items-center justify-center gap-2 text-white/90 text-[13px]">
              <ArrowDown className="h-4 w-4 text-[var(--abs-orange)]" />
              <span>{t.cta.makeItHappen}</span>
            </div>
          </div>
        </div>
      </section>

      {/* PwC-style "Tech is rapidly transforming our world" section */}
      <section className="bg-[var(--abs-cream)] py-24 md:py-32 overflow-hidden" data-testid="home-transforming">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 lg:col-span-7">
              <ScrollReveal>
                <h2 className="serif text-5xl md:text-6xl lg:text-[88px] leading-[1.02] tracking-tight">
                  {transform.title}
                </h2>
                <p className="mt-10 text-[17px] leading-relaxed max-w-md text-[var(--abs-ink)]">
                  {transform.intro}
                </p>
              </ScrollReveal>
            </div>
          </div>

          {/* Two staggered cards */}
          <div className="mt-16 grid grid-cols-12 gap-6 items-end">
            <ScrollReveal className="col-span-12 md:col-span-6 lg:col-span-5 lg:col-start-3" delay={80}>
              <div className="bg-white p-10 lg:p-12 relative">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-ink)]">{transform.insight.kicker}</div>
                <div className="h-px w-full bg-[var(--abs-line)] mt-5 mb-12" />
                <div className="serif text-[120px] lg:text-[160px] leading-[0.85] text-[var(--abs-orange)] font-black tracking-tight">
                  {transform.insight.stat}
                </div>
                <p className="serif text-[26px] md:text-[30px] leading-[1.2] mt-10 text-[var(--abs-ink)]">
                  {transform.insight.body}
                </p>
                <div className="mt-10">
                  <Link to={pathFor("about", lang)} className="border border-[var(--abs-orange)] h-9 w-9 grid place-items-center hover:bg-[var(--abs-orange)] hover:text-white transition-colors">
                    <Plus className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal className="col-span-12 md:col-span-6 lg:col-span-5 lg:-translate-y-24" delay={150}>
              <div className="bg-[#F1ECE5] p-10 lg:p-12">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--abs-ink)]">{transform.featured.kicker}</div>
                <div className="h-px w-full bg-[var(--abs-line)]/70 mt-5 mb-10" />
                <h3 className="serif text-[30px] md:text-[36px] leading-[1.15] text-[var(--abs-ink)]">
                  {transform.featured.title}
                </h3>
                <p className="mt-8 text-[15.5px] leading-[1.7] text-[var(--abs-mute)] max-w-sm">
                  {transform.featured.body}
                </p>
                <div className="mt-9 img-frame h-[220px] bg-white">
                  <img src={PAGE_IMAGES[transform.featured.linkId]} alt={transform.featured.title} className="w-full h-full object-cover" />
                </div>
                <Link to={pathFor(transform.featured.linkId, lang)} className="mt-9 inline-flex items-center justify-center border border-[var(--abs-orange)] h-9 w-9 hover:bg-[var(--abs-orange)] hover:text-white transition-colors">
                  <Plus className="h-4 w-4" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Pillar cards section (the existing "three cards") */}
      <section className="bg-[var(--abs-cream)] pb-24 md:pb-32" data-testid="home-pillars">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <ScrollReveal>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl leading-[1.04]">
                {lang === "zh" ? "为什么选择 ABS Solutions" : "Why choose ABS Solutions"}
              </h2>
              <p className="mt-6 text-[16px] leading-relaxed text-[var(--abs-mute)] max-w-md">{c.intro}</p>
              <Link to={pathFor("services-pricing", lang)} className="abs-btn mt-9" data-testid="pillars-cta">
                {t.cta.viewPricing} <ArrowRight className="h-4 w-4 arrow" />
              </Link>
            </ScrollReveal>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-5">
            {c.cards.map((card, idx) => (
              <ScrollReveal key={idx} delay={idx * 80} className={`bg-white p-7 ${idx === 1 ? "md:translate-y-12" : ""} lift-card`}>
                <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--abs-orange)] mb-5">{card.kicker}</div>
                <div className="h-px w-12 bg-[var(--abs-line)] mb-6" />
                <h3 className="serif text-2xl md:text-[26px] leading-snug">{card.heading}</h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[var(--abs-mute)]">{card.body}</p>
                <div className="mt-7"><span className="border border-[var(--abs-orange)] h-7 w-7 grid place-items-center inline-flex"><Plus className="h-3 w-3" /></span></div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sharp takes / insights */}
      <section className="bg-[var(--abs-beige)] py-24 md:py-32" data-testid="home-insights">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10">
          <ScrollReveal>
            <h2 className="serif text-5xl md:text-6xl lg:text-7xl leading-[1.04] max-w-3xl">{c.insightsHeading}</h2>
          </ScrollReveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {c.insightsIds.map((id, idx) => {
              const detail = (lang === "zh" ? ZH : EN)[id];
              const big = idx === 0;
              return (
                <ScrollReveal key={id} delay={idx * 60} className={big ? "md:row-span-2" : ""}>
                  <Link to={pathFor(id, lang)} className="block group lift-card" data-testid={`insight-${id}`}>
                    <div className={`img-frame ${big ? "h-[420px] md:h-[560px]" : "h-[220px]"} bg-[var(--abs-soft)]`}>
                      <img src={PAGE_IMAGES[id]} alt={detail?.h1 || ""} className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-white p-6 md:p-7">
                      <h3 className="serif text-xl md:text-[24px] leading-snug group-hover:text-[var(--abs-orange)] transition-colors">
                        {detail?.h1 || t.pageNames[id]}
                      </h3>
                      <p className="mt-3 text-[14.5px] text-[var(--abs-mute)] line-clamp-3">{detail?.intro}</p>
                    </div>
                  </Link>
                </ScrollReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audit CTA banner */}
      <section className="py-24 bg-white border-t border-[var(--abs-line)]">
        <div className="max-w-[1480px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <ScrollReveal>
            <div className="accent-bar text-[12px] tracking-[0.2em] uppercase text-[var(--abs-orange)] mb-5">{t.misc.featuredInsight}</div>
            <h2 className="serif text-4xl md:text-5xl leading-[1.08]">
              {lang === "zh" ? "需要一位值得信赖的审计师？" : "Need an auditor you can trust?"}
            </h2>
            <p className="mt-6 text-[16px] text-[var(--abs-mute)] max-w-md">
              {lang === "zh"
                ? "我们为律师事务所、房地产经纪、SMSF 及非营利组织提供专业审计服务——准确、及时、无忧。"
                : "Professional auditing for conveyancers, real estate agents, SMSFs and not-for-profit organisations. Accurate. Timely. Hassle-free."}
            </p>
            <div className="mt-8 flex gap-4">
              <Link to={pathFor("services-pricing", lang)} className="abs-btn abs-btn-solid">
                {t.cta.viewPricing} <ArrowRight className="h-4 w-4 arrow" />
              </Link>
              <Link to={pathFor("contact", lang)} className="abs-btn">
                {t.cta.contactUs} <ArrowRight className="h-4 w-4 arrow" />
              </Link>
            </div>
          </ScrollReveal>
          <ScrollReveal delay={120}>
            <div className="grid grid-cols-2 gap-4">
              {["Conveyancers", "Real Estate", "SMSFs", "Not-for-Profit"].map((label, i) => (
                <div key={label} className={`border border-[var(--abs-line)] p-7 ${i % 2 === 1 ? "translate-y-6" : ""} bg-[var(--abs-soft)]`}>
                  <div className="serif text-2xl">{label}</div>
                  <div className="mt-2 text-xs uppercase tracking-[0.2em] text-[var(--abs-orange)]">Trust account audits</div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Google Reviews — ByNext-style testimonials */}
      <GoogleReviews heading={c.reviewsHeading} reviews={c.reviews} />

      {/* Video showcase — Caraway-style swipeable vertical videos */}
      <VideoShowcase
        kicker={c.videosKicker}
        title={c.videosTitle}
        intro={c.videosIntro}
        videos={c.videos}
      />

      {/* Lead-gen CTA — PwC services bottom style */}
      <LeadCTA />
    </div>
  );
}
